import type { HistoryRange } from "../api/types.js";
import { RANGE_WORDS, RANGES, type ChartModel } from "../app/invest-model.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";

/**
 * The Invest tab's chart (DCTA-229; spec 10-invest §6.1, shared.css §15.3; the mock-up's chart(), readAt(), bindChart()):
 * one series in the bank's ink on the page's own background — a 2 px line, its flat wash, the end dot, the high and the low
 * labelled in place, the first and last day under it. Every figure, label and date is the model's (the bank's strings); the
 * one thing done here with a number is geometry: where a point sits.
 *
 * Reading it: a horizontal DRAG follows the finger 1:1 (nearest point, no easing) once it is a drag (8 px, more across than
 * down) and returns to now when it lifts; a TAP reads the day it touched when the finger lifts and keeps it until a tap
 * elsewhere, Escape or focus leaving (WCAG 2.5.7's single-pointer alternative). Nothing is read on touch-down, so a touch
 * that becomes the page's scroll never flashes a reading (the mock-up read on touch-down; spec §6.1 does not ask it); the
 * plot is a SLIDER — Left / Right one point, Home / End, Escape — whose value text is the reading. A mostly vertical move is
 * the page's scroll (touch-action: pan-y in the design's CSS), never a reading. `onRead(i)` tells the hero which day is
 * read; `onRead(null)` that it is now again.
 *
 * Text is set with textContent (el()), SVG node by node: nothing here parses markup. Positions are in % of the plot, so
 * the marks stay on the line whatever height the plot is drawn at; the viewBox height is the token's (176 / 148).
 */
const NS = "http://www.w3.org/2000/svg";
/** The plot's inner padding (--chart-pad): the high and low labels sit inside the plot. */
const PAD = 28;
/** A horizontal move longer than this, and longer than it is tall, is a drag (the mock-up's 8 px). */
const DRAG_PX = 8;
let seq = 0;

export interface LineChart {
  readonly element: HTMLElement;
  /** The bank is answering a new range: the frame stays, the old line held at 40 % (`[data-busy]`). */
  busy(on: boolean): void;
  /** The chart leaves the page: a tapped reading's document listener goes with it. */
  dispose(): void;
}

export interface ChartOptions {
  /** The plot's height in px (--chart-h 176, --chart-h-sheet 148): the drawing's own coordinate system. */
  readonly height: number;
  readonly onRead?: (index: number | null) => void;
}

/** The nearest point to a pointer at `x` on a plot from `left`, `width` wide, of `n` points (clamped; a zero-width plot reads now). */
export function indexAt(x: number, left: number, width: number, n: number): number {
  if (n <= 1) return 0;
  if (width <= 0) return n - 1;
  return Math.max(0, Math.min(n - 1, Math.round(((x - left) / width) * (n - 1))));
}

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Readonly<Record<string, string>>): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

/** The chart, drawn and readable. */
export function lineChart(m: ChartModel, o: ChartOptions): LineChart {
  const H = o.height;
  const n = m.points.length;
  // Geometry only: the bank's decimal strings as cents, for where a point sits — never shown, never summed.
  const cents = m.points.map((p) => Math.round(Number(p.figure) * 100));
  const lo = Math.min(...cents);
  const span = Math.max(...cents) - lo;
  const X = (i: number): number => (n === 1 ? 500 : (i * 1000) / (n - 1));
  // One value all through is no slope: the line sits mid-plot (§11), never on the low edge.
  const Y = (i: number): number => (span === 0 ? H / 2 : PAD + (H - 2 * PAD) * (1 - ((cents[i] ?? lo) - lo) / span));
  const left = (i: number): string => `${String(X(i) / 10)}%`;
  const top = (i: number): string => `${String(+((Y(i) / H) * 100).toFixed(3))}%`;

  const line = `M${m.points.map((_, i) => `${X(i).toFixed(1)} ${Y(i).toFixed(1)}`).join(" L")}`;
  const drawing = svg("svg", { class: "ln", viewBox: `0 0 1000 ${String(H)}`, preserveAspectRatio: "none", "aria-hidden": "true", focusable: "false" });
  drawing.append(svg("path", { class: "w", d: `${line} L1000 ${String(H)} L0 ${String(H)} Z` }), svg("path", { class: "l", d: line }));

  // Selective direct labels (dataviz): the high above its point, the low below; at an edge they anchor to that edge.
  const label = (x: { readonly index: number; readonly text: string } | undefined, below: boolean): HTMLElement | null => {
    if (x === undefined || x.index < 0 || x.index >= n) return null;
    const node = el("span", { class: "hl", aria: { hidden: "true" } }, [x.text]);
    const at = X(x.index) / 10;
    node.style.left = `${String(at)}%`;
    node.style.top = top(x.index);
    node.style.marginTop = below ? "9px" : "-25px";
    node.style.transform = at < 14 ? "translateX(0)" : at > 86 ? "translateX(-100%)" : "translateX(-50%)";
    return node;
  };
  const now = el("span", { class: "now", aria: { hidden: "true" } });
  now.style.left = "100%";
  now.style.top = top(n - 1);
  const hair = el("span", { class: "xh", aria: { hidden: "true" } });
  const dot = el("span", { class: "pt", aria: { hidden: "true" } });

  seq += 1;
  const captionId = `chart-sum-${String(seq)}`;
  const last = m.points[n - 1];
  const plot = el(
    "div",
    {
      class: "plot",
      role: "slider",
      tabIndex: 0,
      aria: {
        label: m.label,
        describedby: captionId,
        valuemin: "0",
        valuemax: String(n - 1),
        valuenow: String(n - 1),
        valuetext: last?.text ?? "",
      },
    },
    [drawing, label(m.high, false), label(m.low, true), now, hair, dot],
  );
  const first = m.points[0];
  const figure = el("figure", { class: "chart", data: { reading: "false", busy: "false" } }, [
    plot,
    el("div", { class: "xa", aria: { hidden: "true" } }, [el("span", {}, [first?.axis ?? ""]), el("span", {}, [last?.axis ?? ""])]),
    el("figcaption", { class: "vis-hidden", id: captionId }, [m.summary]),
    el("table", { class: "vis-hidden" }, [
      el("caption", {}, [m.label]),
      el("thead", {}, [el("tr", {}, [el("th", { scope: "col" }, ["Day"]), el("th", { scope: "col" }, [m.column])])]),
      el("tbody", {}, m.points.map((p) => el("tr", {}, [el("td", {}, [p.axis]), el("td", {}, [p.money])]))),
    ]),
  ]);

  // ---- reading
  let cur = n - 1;
  /** The plot is measured once per gesture (a move never forces a layout). */
  let gesture: { readonly x0: number; readonly y0: number; readonly id: number; readonly rect: DOMRect; drag: boolean } | undefined;
  let tapped = false;
  const read = (i: number): void => {
    // A finger that moves within one point changes nothing on screen: no write, no repaint.
    if (i === cur && figure.dataset["reading"] === "true") return;
    cur = i;
    hair.style.left = left(i);
    dot.style.left = left(i);
    dot.style.top = top(i);
    figure.dataset["reading"] = "true";
    plot.setAttribute("aria-valuenow", String(i));
    plot.setAttribute("aria-valuetext", m.points[i]?.text ?? "");
    o.onRead?.(i);
  };
  const outside = (e: Event): void => {
    if (!(e.target instanceof Node) || !plot.contains(e.target)) release();
  };
  const release = (): void => {
    tapped = false;
    document.removeEventListener("pointerdown", outside, true);
    cur = n - 1;
    if (figure.dataset["reading"] !== "true") return;
    figure.dataset["reading"] = "false";
    plot.setAttribute("aria-valuenow", String(n - 1));
    plot.setAttribute("aria-valuetext", last?.text ?? "");
    o.onRead?.(null);
  };
  const at = (e: PointerEvent, r: DOMRect): number => indexAt(e.clientX, r.left, r.width, n);

  plot.addEventListener("pointerdown", (e) => {
    if (e.button > 0) return;
    gesture = { x0: e.clientX, y0: e.clientY, id: e.pointerId, rect: plot.getBoundingClientRect(), drag: false };
  });
  plot.addEventListener("pointermove", (e) => {
    const g = gesture;
    if (g === undefined) return;
    const dx = Math.abs(e.clientX - g.x0);
    if (!g.drag && dx > DRAG_PX && dx > Math.abs(e.clientY - g.y0)) {
      g.drag = true;
      try {
        plot.setPointerCapture(g.id);
      } catch {
        // A pointer the page no longer owns: the drag reads on without the capture.
      }
    }
    if (g.drag) read(at(e, g.rect));
  });
  plot.addEventListener("pointerup", () => {
    const g = gesture;
    gesture = undefined;
    if (g === undefined) return;
    if (g.drag) {
      release();
      return;
    }
    // A tap reads the day it touched, and keeps it until a tap elsewhere, Escape or blur.
    read(indexAt(g.x0, g.rect.left, g.rect.width, n));
    if (!tapped) document.addEventListener("pointerdown", outside, true);
    tapped = true;
  });
  plot.addEventListener("pointercancel", () => {
    gesture = undefined;
    release();
  });
  plot.addEventListener("keydown", (e) => {
    let next: number | undefined;
    if (e.key === "ArrowLeft") next = Math.max(0, cur - 1);
    else if (e.key === "ArrowRight") next = Math.min(n - 1, cur + 1);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    else if (e.key === "Escape") {
      // Escape while a day is read ends the reading only; the next Escape is the layer's (a sheet's dialog closes on it).
      if (figure.dataset["reading"] === "true") {
        e.preventDefault();
        e.stopPropagation();
      }
      release();
      return;
    }
    if (next === undefined) return;
    e.preventDefault();
    read(next);
  });
  plot.addEventListener("blur", () => {
    if (gesture === undefined) release();
  });

  return {
    element: figure,
    busy: (on) => {
      figure.dataset["busy"] = on ? "true" : "false";
    },
    dispose: () => {
      tapped = false;
      document.removeEventListener("pointerdown", outside, true);
    },
  };
}

/**
 * The range control (§6.2; shared.css 10.11, 15.4): four views of one chart, pressed-state buttons in one labelled group,
 * each named by its visible label and its words (", 1 month": WCAG 2.5.3). A press on another range moves the pressed
 * state at once and asks for it; a press on the one shown asks nothing. `set` follows a range chosen elsewhere.
 */
export function rangeControl(shown: HistoryRange, onPick: (range: HistoryRange) => void): { readonly node: HTMLElement; set(range: HistoryRange): void } {
  let current = shown;
  const buttons = RANGES.map((r) =>
    el("button", { class: "raw", type: "button", data: { range: r }, aria: { pressed: r === shown ? "true" : "false" } }, [
      RANGE_WORDS[r].label,
      el("span", { class: "vis-hidden" }, [`, ${RANGE_WORDS[r].name}`]),
    ]),
  );
  const set = (range: HistoryRange): void => {
    current = range;
    buttons.forEach((b, i) => b.setAttribute("aria-pressed", RANGES[i] === range ? "true" : "false"));
  };
  buttons.forEach((b, i) =>
    b.addEventListener("click", () => {
      const r = RANGES[i] as HistoryRange;
      if (r === current) return;
      set(r);
      onPick(r);
    }),
  );
  return { node: el("div", { class: "seg rng", role: "group", aria: { label: "Chart range" } }, buttons), set };
}

/** Loading: the plot at its own height with one quiet block in it (never an empty axis frame); the dates' line kept. */
export function chartSkeleton(): HTMLElement {
  // `skel-mask`: its placeholder dates are bars wherever it stands (the tab masks them, the holding sheet would not).
  return el("figure", { class: "chart skel-mask", aria: { hidden: "true" } }, [
    el("div", { class: "plot" }, [el("span", { class: "sk skl" })]),
    el("div", { class: "xa" }, [el("span", {}, ["00 Sep"]), el("span", {}, ["00 Sep"])]),
  ]);
}

/** The history could not be read: the plot keeps its height, one line says so, Retry (44 px); the rest of the tab stays. */
export function chartError(onRetry: () => void): HTMLElement {
  const retry = el("button", { class: "raw", type: "button" }, ["Retry"]);
  retry.addEventListener("click", () => onRetry());
  return el("figure", { class: "chart" }, [
    el("div", { class: "plot" }, [el("div", { class: "cerr" }, [icon("i-alert"), el("span", {}, ["Can't show the chart right now."]), retry])]),
    el("div", { class: "xa", aria: { hidden: "true" } }, [el("span", {}, [" "]), el("span", {}, [" "])]),
  ]);
}
